import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "../../components/ui/sheet/index.js";
import {
  contactSheetBodyClass,
  contactSheetDescriptionClass,
  contactSheetHeaderClass,
  contactSheetTitleClass,
} from "../contacts/contact-sheet-presentation.js";
import { CONTACT_SHEET_OVERLAY_MOTION_CLASS } from "../contacts/contacts-table-presentation.js";
import { cn } from "../../lib/utils.js";

/**
 * Shared Reports filter sheet — opens from the physical LEFT in LTR and RTL.
 * Uses Multi-Chat surface tokens (no dark foreign chrome).
 */
export function ReportFilterSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  testId,
  activeCount = 0,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  children: React.ReactNode;
  testId: string;
  activeCount?: number;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className={cn(
          "flex h-full max-h-dvh w-[min(22rem,92vw)] flex-col gap-0 overflow-hidden border-e border-border/60 bg-surface p-0 text-foreground shadow-lg",
        )}
        overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
        data-testid={testId}
      >
        <header className={contactSheetHeaderClass}>
          <div className="grid min-w-0 gap-1">
            <SheetTitle className={contactSheetTitleClass}>{title}</SheetTitle>
            <SheetDescription className={contactSheetDescriptionClass}>
              {description}
            </SheetDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
            onClick={() => onOpenChange(false)}
            aria-label={t("common.close")}
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        </header>
        <div className={cn(contactSheetBodyClass, "flex flex-col gap-3")}>{children}</div>
        {activeCount > 0 ? (
          <p className="m-0 border-t border-border/60 px-4 py-2 text-[0.75rem] text-muted">
            {t("reports.chrome.activeFilters", { count: activeCount })}
          </p>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

export function ReportOpenFiltersButton({
  onClick,
  activeCount = 0,
  testId,
  label,
}: {
  onClick: () => void;
  activeCount?: number;
  testId: string;
  label: string;
}): React.JSX.Element {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-9 shrink-0 gap-1.5 md:hidden"
      onClick={onClick}
      data-testid={testId}
    >
      <SlidersHorizontal size={14} aria-hidden="true" />
      {label}
      {activeCount > 0 ? (
        <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[0.6875rem] font-semibold text-white tabular-nums">
          {activeCount}
        </span>
      ) : null}
    </Button>
  );
}

export function useReportFiltersSheet(): {
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
} {
  const [filtersOpen, setFiltersOpen] = useState(false);
  return { filtersOpen, setFiltersOpen };
}
